import { prefersReducedMotion } from '@theme/utilities';

/**
 * <spv-social-proof>
 *
 * Thin wrapper around Slyde (loaded as a UMD global via assets/slyde.umd.js).
 * Slyde owns the carousel mechanics — infinite loop with leading + trailing
 * clones, drag/swipe, snap, autoplay. We only:
 *   1. Translate section settings (autoplay enabled/interval) into Slyde options.
 *   2. Wire `slideChange` to update our counter and restart the progress-bar
 *      keyframe animation so the bar visually tracks the autoplay tick.
 *   3. Pause autoplay on focus / document.hidden / prefers-reduced-motion.
 *      (Slyde already handles `pauseOnHover` natively.)
 */

class SpvSocialProof extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-slyde-root]');
    this.counterCurrent = this.querySelector('[data-counter-current]');
    this.counterTotal = this.querySelector('[data-counter-total]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.pagination = this.querySelector('[data-pagination]');
    if (!this.root) return;

    const slidesEl = this.root.querySelectorAll('.slyde-slide');
    const slideCount = slidesEl.length;
    if (slideCount === 0) return;

    this.reducedMotion = prefersReducedMotion();
    const autoplayEnabled = this.dataset.autoplayEnabled === 'true' && slideCount > 1 && !this.reducedMotion;
    const autoplayInterval = parseFloat(this.dataset.autoplayInterval || '5') * 1000;

    if (this.counterTotal) this.counterTotal.textContent = pad2(slideCount);

    if (slideCount <= 1) {
      if (this.pagination) this.pagination.hidden = true;
      return;
    }

    // Slyde global is defined by assets/slyde.umd.js. Initialize on next tick
    // if the script hasn't parsed yet (defer="defer" + module ordering should
    // already have it ready, but we belt-and-suspenders this).
    if (window.Slyde && window.Slyde.Slyde) {
      this.#initSlyde({ autoplayEnabled, autoplayInterval });
    } else {
      const ready = () => {
        if (window.Slyde && window.Slyde.Slyde) {
          this.#initSlyde({ autoplayEnabled, autoplayInterval });
        } else {
          setTimeout(ready, 50);
        }
      };
      ready();
    }
  }

  disconnectedCallback() {
    try { this.slider?.destroy(); } catch {}
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.progressFill?.removeEventListener('animationend', this.#onProgressEnd);
  }

  #initSlyde({ autoplayEnabled, autoplayInterval }) {
    const { Slyde, Autoplay, Pagination, Navigation, Scrollbar, Thumbnails, Keyboard, Video } = window.Slyde;
    // Register only the autoplay module — we render our own counter + progress
    // bar instead of using Slyde's built-in pagination/scrollbar/etc.
    Slyde.use([Autoplay]);

    this.slider = new Slyde(this.root, {
      loop: true,
      navigation: false,
      pagination: false,
      thumbnails: false,
      scrollbar: false,
      keyboard: false,
      video: false,
      autoplay: autoplayEnabled
        ? { delay: autoplayInterval, pauseOnHover: true }
        : false,
      slidesPerView: 1,
      spaceBetween: 12,
      // Centering padding: (viewport - card) / 2. Mobile card = 300px;
      // desktop card = (viewport - 24) / 3, so padding = viewport/3 + 4.
      padding: 'calc(50% - 150px)',
      breakpoints: {
        990: {
          padding: 'calc(100% / 3 + 4px)',
        },
      },
      speed: 350,
    });

    // Wire counter + progress-bar to Slyde's slideChange event.
    this.slider.events.on('slideChange', this.#onSlideChange);
    this.#updateCounter();

    // Progress bar: CSS keyframe restarted on each slideChange. Animation
    // duration = autoplay interval. If autoplay is disabled, the bar stays
    // static (filled at 100% for reduced-motion, empty otherwise).
    if (autoplayEnabled) {
      this.style.setProperty('--autoplay-interval', `${autoplayInterval}ms`);
      this.dataset.autoplay = 'running';
    } else if (this.reducedMotion) {
      this.dataset.autoplay = 'reduced';
    }

    // Pause sources Slyde doesn't handle natively.
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onSlideChange = () => {
    this.#updateCounter();
    this.#restartProgress();
  };

  #updateCounter() {
    if (!this.counterCurrent || !this.slider) return;
    // Slyde's currentIndex is 0-based over real slides (clones excluded).
    const idx = ((this.slider.currentIndex % this.slider.totalSlides) + this.slider.totalSlides) % this.slider.totalSlides;
    this.counterCurrent.textContent = pad2(idx + 1);
  }

  #restartProgress() {
    if (!this.progressFill || this.dataset.autoplay === 'reduced') return;
    // Toggle the data attribute off-then-on with a forced reflow so the CSS
    // keyframe restarts from scaleX(0).
    const wasPaused = this.dataset.autoplay === 'paused';
    this.dataset.autoplay = 'reset';
    void this.progressFill.offsetWidth;
    this.dataset.autoplay = wasPaused ? 'paused' : 'running';
  }

  #pauseAutoplay() {
    if (!this.slider) return;
    const ap = this.slider.modules?.find((m) => m.name === 'autoplay');
    ap?.stop();
    if (this.dataset.autoplay === 'running') this.dataset.autoplay = 'paused';
  }

  #resumeAutoplay() {
    if (!this.slider) return;
    if (this.dataset.autoplay !== 'paused') return;
    if (document.hidden) return;
    if (this.matches(':focus-within')) return;
    const ap = this.slider.modules?.find((m) => m.name === 'autoplay');
    ap?.start();
    this.dataset.autoplay = 'running';
  }

  #onFocusIn = () => this.#pauseAutoplay();
  #onFocusOut = (e) => {
    if (this.contains(e.relatedTarget)) return;
    this.#resumeAutoplay();
  };
  #onVisibilityChange = () => {
    if (document.hidden) this.#pauseAutoplay();
    else this.#resumeAutoplay();
  };
  #onProgressEnd = () => {};
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

if (!customElements.get('spv-social-proof')) {
  customElements.define('spv-social-proof', SpvSocialProof);
}
